import { useEffect, useState, useCallback, useRef } from 'react'
import { usePage } from '@inertiajs/react'
import { AlertTriangle, Check, X } from '@/components/ui/icons'
import { focusRing } from '@/components/ui/buttonStyles'
import { motion } from '@/utils/motion'
import { cn } from '@/utils/cn'
import { SharedProps } from '@/types'

const TOAST_DURATION_MS = 5000

/**
 * The transient half of the app's outcome vocabulary; `Notice` is the half that
 * stays on the page. Both read as a rail down the leading edge, a glyph, and the
 * message in the reading serif.
 *
 * Two things this deliberately does not do. It does not repaint itself in a
 * signal colour — an alert used to arrive as a bright red slab, which was the
 * loudest thing on a page whose whole palette is warm and quiet. And it does not
 * size itself to its message, so a two-word confirmation and a sentence-long
 * failure arrive as the same object in the same place rather than as a chip and
 * a paragraph.
 *
 * It also carries no draining countdown bar. One was tried: at a hairline it read
 * as a broken underline, and at any weight that read as time it put a deadline on
 * a page whose whole point is being unhurried.
 */
export function FlashBanner() {
  const { flash } = usePage<SharedProps>().props
  const message = flash?.alert ?? flash?.notice
  const isAlert = !!flash?.alert

  const [visible, setVisible] = useState(false)
  const band = useRef<HTMLDivElement>(null)

  const dismiss = useCallback(() => {
    const el = band.current
    if (!el) return setVisible(false)
    void motion.sink(el).then(() => setVisible(false))
  }, [])

  useEffect(() => {
    setVisible(!!message)
    if (!message) return
    const id = setTimeout(dismiss, TOAST_DURATION_MS)
    return () => clearTimeout(id)
  }, [message, dismiss])

  useEffect(() => {
    if (visible && band.current) void motion.rise(band.current)
  }, [visible])

  if (!visible || !message) return null

  const Glyph = isAlert ? AlertTriangle : Check

  return (
    <div className="fixed inset-x-0 bottom-4 z-50 flex justify-center px-4 sm:bottom-6 pointer-events-none">
      <div
        ref={band}
        role={isAlert ? 'alert' : 'status'}
        aria-live={isAlert ? 'assertive' : 'polite'}
        className={cn(
          'pointer-events-auto w-[min(30rem,calc(100vw-2rem))]',
          'flex items-start gap-3 rounded-lg px-4 py-3.5 sm:px-5',
          'bg-toast-bg text-toast-text border border-toast-border border-l-[3px] shadow-lg',
          isAlert ? 'border-l-toast-rail-alert' : 'border-l-toast-rail-notice',
        )}
      >
        <Glyph
          size={16}
          className={cn('mt-0.5 shrink-0', isAlert ? 'text-toast-rail-alert' : 'text-toast-rail-notice')}
        />

        <p className="min-w-0 flex-1 font-serif text-sm leading-relaxed">{message}</p>

        <button
          onClick={dismiss}
          aria-label="Dismiss notification"
          className={cn(
            'shrink-0 -my-2.5 -mr-2 min-h-11 min-w-11 flex items-center justify-center rounded-md',
            'text-current opacity-55 hover:opacity-100 transition-opacity duration-200',
            focusRing,
          )}
        >
          <X size={15} />
        </button>
      </div>
    </div>
  )
}
